import React from 'react';
import PropTypes from 'prop-types';

import NativeImage from '$partials/media/native-image.jsx';
import { PersonItem } from '$partials/listings';
import { formatUrlWithActiveFund } from '$utilities/format';
import { getSidebarTitles } from '$utilities/sidebar-titles';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * CompanyPage Modal
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CompanyPage = ({ page }) => {
    return (
        <div className='overflow-hidden rounded-md'>
            <main
                className='bg-white min-h-screen relative'
                id='main-content'>
                <div className='max-w-container bg-white'>
                    <div className='modal-container-padding-right flex flex-wrap max-w-container'>
                        {/* Left Sidebar */}
                        <div className='w-sidebar' />

                        {/* Main content */}
                        <div className='flex-1 flex flex-wrap self-start layout-negative-margin'>
                            <section className='w-full'>
                                <div className=''>
                                    <div className='mb-30'>
                                        {page?.listingImage ? (
                                            <NativeImage
                                                className='object-contain object-left-top h-[50px] w-full'
                                                {...page.listingImage} />
                                        ) : (
                                            <div
                                                className='type-style-4'
                                                style={{ fontWeight: '400' }}>
                                                {page?.title}
                                            </div>
                                        )}

                                        {page?.companyStatus === 'Acquired' && page?.acquiredByText && (
                                            <p className='type-style-6'>Acquired by {page?.acquiredByText}</p>
                                        )}

                                        {page?.companyStatus === 'Publicly listed' && page?.publiclyListedOnText && (
                                            <p className='type-style-6'>Pubicly listed on {page?.publiclyListedOnText}</p>
                                        )}
                                    </div>

                                    <div className=''>
                                        <div className='layout-item-padding layout-negative-margin'>
                                            {/* Standfirst */}
                                            <div className='sm:w-10/12'>
                                                <h1 className='type-style-3'>
                                                    {page?.standfirst}
                                                </h1>
                                            </div>
                                            <div className='flex mb-50 mt-30 type-style-8'>
                                                {page?.websiteUrl && (
                                                    <>
                                                        <a
                                                            className=''
                                                            href={page?.websiteUrl}
                                                            rel='noreferrer noopener'
                                                            target='_blank'>
                                                            {'Website'}
                                                        </a>
                                                        {page?.linkedinUrl && (
                                                            <span>&nbsp;·&nbsp;</span>
                                                        )}
                                                    </>
                                                )}
                                                {page?.linkedinUrl && (
                                                    <>
                                                        <a
                                                            className=''
                                                            href={page?.linkedinUrl}
                                                            rel='noreferrer noopener'
                                                            target='_blank'>
                                                            {'LinkedIn'}
                                                        </a>
                                                        {page?.twitterUrl && (
                                                            <span>&nbsp;·&nbsp;</span>
                                                        )}
                                                    </>
                                                )}
                                                {page?.twitterUrl && (
                                                    <>
                                                        <a
                                                            className=''
                                                            href={page?.twitterUrl}
                                                            rel='noreferrer noopener'
                                                            target='_blank'>
                                                            {'X'}
                                                        </a>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Spacer */}
                        <div className='mt-60'/>

                        <div className='max-w-container bg-white rounded-md'>
                            <div className='flex max-w-container sm:pb-50 mb:pb-40 md:pt80'>
                                <div className='w-sidebar'>
                                    <div className='flex flex-col items-center'>
                                    </div>
                                </div>
                                <div className='flex-1'>
                                    <div className='md:-ml-10'>
                                        {/* Funds */}
                                        <div className='pb-10 flex flex-wrap gap-x-50 md:gap-x-0 -ml-10'>
                                            {page?.funds?.edges.length > 0 && page?.funds?.edges.map(({node: fund}, i) => {
                                                return (
                                                    <div
                                                        className={'block md:inline-block md:pr-15 mb-20'}
                                                        key={i}>
                                                        <a
                                                            className={`type-style-8 ${fund?.branding?.toLowerCase()}-border background-6px pl-20`}
                                                            href={fund.url}
                                                            rel='noopener noreferrer'
                                                            target='_blank'>
                                                            {fund?.title}
                                                        </a>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className='-ml-10'>
                                            {/* Locations */}
                                            <div className='sm:pb-30'>
                                                {page?.locations?.edges.length > 0 && page?.locations?.edges.map(({node: location}, i) => {
                                                    const url = formatUrlWithActiveFund(location?.url);
                                                    return (
                                                        <a
                                                            className='mr-5 mb-5 type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border border-black/25'
                                                            href={url}
                                                            key={i}>
                                                            {location?.label}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                            {/* Categories */}
                                            <div className='pt-30 sm:pt-0 pb-50 sm:pb-0'>
                                                {page?.categories?.edges.length > 0 && page?.categories?.edges.map(({node: category}, i) => {
                                                    const url = formatUrlWithActiveFund(category?.url);
                                                    return (
                                                        <a
                                                            className='mr-5 mb-5 type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border border-black/25'
                                                            href={url}
                                                            key={i}>
                                                            {category?.label}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Founders */}
                            <div className='flex flex-wrap max-w-container mb-30 sm:mb-50 rounded-md'>
                                <div className='w-sidebar'>
                                    <div className='flex flex-col items-center'>
                                        <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl'>
                                            {getSidebarTitles()?.companyFoundersSidebarTitle}
                                        </h2>
                                    </div>
                                </div>
                                <section
                                    aria-label='Company'
                                    className='-ml-10 flex-1 flex flex-wrap self-start'
                                    role='feed'>
                                    <ul className='w-full grid grid-cols-1 md:grid-cols-3 layout-gap gap-y-30 sm:gap-y-25 md:gap-y-30'>
                                        {page?.founders?.edges.length > 0 && page?.founders?.edges.map(({node: item}) => {
                                            return (
                                                <li
                                                    key={item.id}>
                                                    <PersonItem {...item}/>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </section>
                            </div>

                            {/* Details */}
                            <div className='flex max-w-container sm:pb-80 md:pb-100'>
                                <div className='w-sidebar'>
                                    <div className='flex flex-col items-center'>
                                        <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl'>
                                            {getSidebarTitles()?.companyDetailsSidebarTitle}
                                        </h2>
                                    </div>
                                </div>
                                {/* Partners */}
                                <div className='-ml-10 flex-1'>
                                    <div className='w-full grid grid-cols-1 md:grid-cols-3 layout-gap'>
                                        <div className='pb-40 sm:pb-40'>
                                            <p className='type-style-8 mb-10'>Our team</p>
                                            <div>
                                                {page?.partners?.edges.length > 0 && page?.partners?.edges.map(({node: partner}, i, { length }) => {
                                                    const isLast = i ===  length - 1;
                                                    const url = formatUrlWithActiveFund(partner.url);
                                                    return (
                                                        <div
                                                            className='type-style-4 flex-shrink-0'
                                                            key={i}>
                                                            <a
                                                                className=''
                                                                href={url}>
                                                                <span>{partner?.title}</span>
                                                            </a>
                                                            {!isLast && (
                                                                <span>,&nbsp;</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        {/* Co-investors */}
                                        {page?.coinvestors?.edges.length > 0 && (
                                            <div className='pb-70 sm:pb-0 md:col-span-2'>
                                                <p className='type-style-8 mb-10'>Co-investors</p>
                                                <div className='flex flex-wrap'>
                                                    {page?.coinvestors?.edges.map(({node: coinvestor}, i, { length }) => {
                                                        const isLast = i === length - 1;
                                                        const showListingLink = coinvestor?.companiesByCoinvestor?.total > 1;
                                                        return (
                                                            <div
                                                                className='type-style-4 flex-shrink-0'
                                                                key={i}>
                                                                {showListingLink && (
                                                                    <a href={coinvestor?.url}>{coinvestor?.label}</a>
                                                                )}
                                                                {!showListingLink && (
                                                                    <span>{coinvestor?.label}</span>
                                                                )}
                                                                {!isLast && (
                                                                    <span>,&nbsp;</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */


const propTypes = {
    page: PropTypes.shape({
        __typename: PropTypes.string,
        title: PropTypes.string,
        standfirst: PropTypes.string,
        coinvestors: PropTypes.object,
        locations: PropTypes.object,
        founders: PropTypes.object,
        partners: PropTypes.object,
        categories: PropTypes.object,
        funds: PropTypes.shape({
            edges: PropTypes.arrayOf(PropTypes.shape({
                node: PropTypes.shape({
                    url: PropTypes.string,
                    title: PropTypes.string,
                    branding: PropTypes.string,
                }),
            }),),
        }),
        listingImage: PropTypes.object,
        linkedinUrl: PropTypes.string,
        websiteUrl: PropTypes.string,
        twitterUrl: PropTypes.string,
        acquiredByText: PropTypes.string,
        companyStatus: PropTypes.string,
        publiclyListedOnText: PropTypes.string,
    })
};

CompanyPage.propTypes = propTypes;

export default CompanyPage;
